import { getManilaCalendarYmd, toDateOnlyYmd } from "../lib/dateTime";

export const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export const MONTH_SHORT_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const YMD_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

export const toShowDateYmd = (value) => toDateOnlyYmd(value);

export const isArchivedEvent = (event) =>
  String(event?.computedStatus || event?.status || "").trim().toLowerCase() === "archived";

export const filterCalendarEvents = (events = [], search = "") => {
  const needle = String(search || "").trim().toLowerCase();
  return (Array.isArray(events) ? events : []).filter((event) => {
    if (!event || isArchivedEvent(event) || !toShowDateYmd(event.date)) return false;
    if (!needle) return true;
    return String(event.title || "").toLowerCase().includes(needle);
  });
};

export const groupEventsByShowDate = (events = []) => {
  const groups = {};
  (Array.isArray(events) ? events : []).forEach((event) => {
    const key = toShowDateYmd(event?.date);
    if (!key) return;
    if (!groups[key]) groups[key] = [];
    groups[key].push(event);
  });

  Object.values(groups).forEach((list) => {
    list.sort((left, right) => {
      const timeCompare = String(left.time || "").localeCompare(String(right.time || ""));
      if (timeCompare !== 0) return timeCompare;
      return String(left.title || "").localeCompare(String(right.title || ""));
    });
  });

  return groups;
};

const pad = (value) => String(value).padStart(2, "0");

const ymdFromUtc = (date) => date.toISOString().slice(0, 10);

export const shiftMonth = (year, month, delta) => {
  const next = new Date(Date.UTC(Number(year), Number(month) - 1 + Number(delta), 1));
  return { year: next.getUTCFullYear(), month: next.getUTCMonth() + 1 };
};

export const getManilaYearMonth = (now = Date.now()) => {
  const ymd = getManilaCalendarYmd(now);
  const match = YMD_PATTERN.exec(ymd);
  if (!match) return { year: 1970, month: 1 };
  return { year: Number(match[1]), month: Number(match[2]) };
};

export const formatMonthTitle = (year, month) => {
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, 1));
  return date.toLocaleDateString("en-PH", {
    timeZone: "UTC",
    month: "long",
    year: "numeric",
  });
};

export const formatShowDateLabel = (ymd) => {
  const match = YMD_PATTERN.exec(toShowDateYmd(ymd));
  if (!match) return "";
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return date.toLocaleDateString("en-PH", {
    timeZone: "UTC",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

/** Sunday-start cells for a 1-based month, including leading and trailing days. */
export const buildMonthGrid = (year, month) => {
  const numericYear = Number(year);
  const numericMonth = Number(month);
  const first = new Date(Date.UTC(numericYear, numericMonth - 1, 1));
  const leading = first.getUTCDay();
  const daysInMonth = new Date(Date.UTC(numericYear, numericMonth, 0)).getUTCDate();
  const cells = [];

  for (let index = 0; index < leading; index += 1) {
    const date = new Date(Date.UTC(numericYear, numericMonth - 1, 1 - (leading - index)));
    const ymd = ymdFromUtc(date);
    cells.push({ ymd, day: date.getUTCDate(), inMonth: false });
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push({
      ymd: `${numericYear}-${pad(numericMonth)}-${pad(day)}`,
      day,
      inMonth: true,
    });
  }

  const cursor = new Date(Date.UTC(numericYear, numericMonth - 1, daysInMonth));
  while (cells.length % 7 !== 0) {
    cursor.setUTCDate(cursor.getUTCDate() + 1);
    cells.push({
      ymd: ymdFromUtc(cursor),
      day: cursor.getUTCDate(),
      inMonth: false,
    });
  }

  return cells;
};

export const monthHasEvents = (groups, year, month) => {
  const prefix = `${Number(year)}-${pad(month)}-`;
  return Object.keys(groups || {}).some((key) => key.startsWith(prefix));
};
